import { useState } from "react";
import { Box, Popover, Typography, useTheme } from "@mui/material";
import { Video, Phone } from "lucide-react";
import { handleContactPhone } from "@/modules/common/utils";
import useLeadContactMethods from "@/modules/lead-page/hooks/lead-info/lead-contact-methods.hook";

const EGYPT_CALLER_ID = "+20248845455";

interface CallPopoverButtonProps {
  phoneNumber?: string;
  children: React.ReactElement;
  onPhoneCall?: () => void;
  onZoomCall?: () => void;
}

const CallPopoverButton = ({
  phoneNumber,
  children,
  onPhoneCall,
  onZoomCall,
}: CallPopoverButtonProps) => {
  const { handleStartCall, isLoading } = useLeadContactMethods();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [copiedNumber, setCopiedNumber] = useState(false);
  const theme = useTheme();

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isLoading) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setCopiedNumber(false);
  };

  const handlePhoneCall = () => {
    handleClose();
    if (onPhoneCall) {
      onPhoneCall();
    } else {
      handleContactPhone(phoneNumber);
      handleStartCall(false);
    }
  };

  const handleZoomCall = async () => {
    if (onZoomCall) {
      handleClose();
      onZoomCall();
      return;
    }

    if (!phoneNumber) return;

    const cleaned = phoneNumber.replace(/[^\d+]/g, "");
    let e164: string;
    if (cleaned.startsWith("+")) e164 = cleaned;
    else if (cleaned.startsWith("00")) e164 = `+${cleaned.slice(2)}`;
    else if (cleaned.startsWith("0")) e164 = `+20${cleaned.slice(1)}`;
    else e164 = `+${cleaned}`;

    try {
      await navigator.clipboard.writeText(e164);
      setCopiedNumber(true);
    } catch {
      // clipboard access denied
    }

    handleStartCall(true);
    const isLocal = e164.startsWith("+20");
    const zoomUrl = isLocal
      ? `zoomphonecall://${e164}?callerid=${EGYPT_CALLER_ID}`
      : `zoomphonecall://${e164}`;
    window.location.href = zoomUrl;

    setTimeout(() => {
      if (document.hidden) return;
      const ua = navigator.userAgent.toLowerCase();
      if (/iphone|ipad|ipod/.test(ua)) {
        window.location.href =
          "https://apps.apple.com/app/zoom-workplace/id546505307";
      } else if (/android/.test(ua)) {
        window.location.href =
          "https://play.google.com/store/apps/details?id=us.zoom.videomeetings";
      } else {
        window.open("https://zoom.us/download", "_blank");
      }
    }, 2500);
  };

  const menuItemSx = {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    px: "16px",
    py: "10px",
    cursor: "pointer",
    userSelect: "none" as const,
    "&:hover": {
      backgroundColor: theme.nawy.colors.semantic.layer.secondary,
    },
  };

  const renderMenu = () => (
    <Box sx={{ py: "4px", minWidth: 160 }}>
      <Box onClick={handleZoomCall} sx={menuItemSx}>
        <Video
          size={16}
          color={theme.nawy.colors.semantic.text.primary}
          strokeWidth={1.75}
        />
        <Typography
          sx={{
            ...theme.nawy.typography.english.variants.body.md.regular,
            color: copiedNumber
              ? theme.nawy.colors.semantic.indicator.green
              : theme.nawy.colors.semantic.text.primary,
          }}
        >
          {"Zoom call"}
        </Typography>
      </Box>

      <Box onClick={handlePhoneCall} sx={menuItemSx}>
        <Phone
          size={16}
          color={theme.nawy.colors.semantic.text.primary}
          strokeWidth={1.75}
        />
        <Typography
          sx={{
            ...theme.nawy.typography.english.variants.body.md.regular,
            color: theme.nawy.colors.semantic.text.primary,
          }}
        >
          Phone call
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      {children && typeof children === "object" && "props" in children ? (
        <children.type
          {...children.props}
          onClick={(e: React.MouseEvent<HTMLElement>) => {
            children.props.onClick?.(e);
            handleClick(e);
          }}
          disabled={children.props.disabled || isLoading}
        />
      ) : null}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: `${theme.nawy.radius.lg}px`,
            marginTop: "6px",
            boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
            border: `1px solid ${theme.nawy.colors.semantic.border.secondary}`,
            overflow: "hidden",
          },
        }}
      >
        {renderMenu()}
      </Popover>
    </>
  );
};

export default CallPopoverButton;
